import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { deleteUser, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth, getUserData, updateUserCustomApiKey, updateUserGptModel } from '../firebase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, SignOut, Trash, Eye, EyeSlash, Robot, Key } from '@phosphor-icons/react';

function Profile() {
  const { currentUser, logout } = useAuth();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [customApiKey, setCustomApiKey] = useState('');
  const [aiQuizCount, setAiQuizCount] = useState(5);
  const [gptModel, setGptModel] = useState('gpt-4o-mini');
  const [isCustomApiKeyUser, setIsCustomApiKeyUser] = useState(false);
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.providerData[0]?.providerId === 'google.com') {
      setIsGoogleUser(true);
    }

    const fetchUserData = async () => {
      if (auth.currentUser) {
        const userData = await getUserData(auth.currentUser.uid);
        setCustomApiKey(userData.customApiKey || '');
        setAiQuizCount(userData.aiQuizCount);
        setGptModel(userData.gptModel || 'gpt-4o-mini');
        setIsCustomApiKeyUser(!!userData.customApiKey);
      }
    };
    fetchUserData();
  }, [currentUser]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setMessage('비밀번호가 성공적으로 변경되었습니다. 다시 로그인해주세요.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
      await logout();
      navigate('/login');
    } catch (error) {
      if (error.code === 'auth/invalid-credential') {
        setError('비밀번호가 맞지 않습니다.');
      } else {
        setError('비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
      }
      console.error("Password change error:", error);
    }
  };

  const handleAccountDelete = async () => {
    if (window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        await deleteUser(currentUser);
        navigate('/');
      } catch (error) {
        setError('계정 삭제에 실패했습니다. 다시 로그인 후 시도해주세요.');
        console.error("Account deletion error:", error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      setError('로그아웃에 실패했습니다.');
      console.error("Logout error:", error);
    }
  };

  const handleApiKeyChange = async (e) => {
    e.preventDefault();
    try {
      await updateUserCustomApiKey(auth.currentUser.uid, customApiKey);
      setMessage('API 키가 성공적으로 업데이트되었습니다.');
      setIsCustomApiKeyUser(!!customApiKey);

      if (!customApiKey) {
        setGptModel('gpt-4o-mini');
        await updateUserGptModel(auth.currentUser.uid, 'gpt-4o-mini');
      }
    } catch (error) {
      setError('API 키 업데이트 중 오류가 발생했습니다.');
    }
  };

  const handleGptModelChange = async (e) => {
    const newModel = e.target.value;
    setGptModel(newModel);
    try {
      await updateUserGptModel(auth.currentUser.uid, newModel);
      setMessage('GPT 모델이 성공적으로 업데이트되었습니다.');
    } catch (error) {
      setError('GPT 모델 업데이트 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="container mx-auto px-4 pt-32 pb-40 min-h-screen bg-gray-50/50">
      <div className="max-w-md mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-8">내 정보 관리</h1>

        {/* Account Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <User size={24} weight="duotone" className="text-primary" />
              </div>
              <div>
                <CardTitle>계정 정보</CardTitle>
                <CardDescription>이메일 및 비밀번호 관리</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">이메일 주소</Label>
              <Input id="email" type="email" value={currentUser.email} disabled className="bg-gray-50" />
            </div>

            {!isGoogleUser && (
              <>
                {!showPasswordForm ? (
                  <Button onClick={() => setShowPasswordForm(true)} variant="outline" className="w-full">
                    <Lock size={18} className="mr-2" weight="duotone" />
                    비밀번호 변경
                  </Button>
                ) : (
                  <form onSubmit={handlePasswordChange} className="space-y-4 pt-4 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">현재 비밀번호</Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="현재 비밀번호"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">새 비밀번호</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="새 비밀번호"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">새 비밀번호 확인</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="새 비밀번호 확인"
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => setShowPasswordForm(false)} className="flex-1">
                        취소
                      </Button>
                      <Button type="submit" className="flex-1">
                        변경 확인
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button onClick={handleLogout} variant="outline" className="w-full">
              <SignOut size={18} className="mr-2" weight="duotone" />
              로그아웃
            </Button>
            <Button onClick={handleAccountDelete} variant="ghost" className="w-full text-red-500 hover:text-red-600 hover:bg-red-50">
              <Trash size={18} className="mr-2" weight="duotone" />
              회원 탈퇴
            </Button>
          </CardFooter>
        </Card>

        {/* AI Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Robot size={24} weight="duotone" className="text-primary" />
              </div>
              <div>
                <CardTitle>AI 설정</CardTitle>
                <CardDescription>
                  {isCustomApiKeyUser
                    ? '사용자 API 키로 무제한 이용 가능'
                    : `남은 AI 퀴즈 생성 횟수: ${aiQuizCount}회`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gptModel">기본 AI 모델 선택</Label>
              <select
                id="gptModel"
                value={gptModel}
                onChange={handleGptModelChange}
                disabled={!isCustomApiKeyUser}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="gpt-4.1">gpt-4.1</option>
                <option value="gpt-4o-mini">gpt-4o-mini</option>
              </select>
              {!isCustomApiKeyUser && (
                <p className="text-xs text-muted-foreground">*API 키 입력 후 AI 모델 선택 가능</p>
              )}
            </div>

            <form onSubmit={handleApiKeyChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="apiKey" className="flex items-center gap-2">
                  <Key size={16} weight="duotone" />
                  OpenAI API 키
                </Label>
                <div className="relative">
                  <Input
                    type={isApiKeyVisible ? "text" : "password"}
                    id="apiKey"
                    value={customApiKey}
                    onChange={(e) => setCustomApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {isApiKeyVisible ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full">
                API 키 업데이트
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Messages */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}
        {message && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
            {message}
          </div>
        )}
      </div>
    </div >
  );
}

export default Profile;